import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { useEffect } from "react";
import { format, parseISO, isValid, formatDistanceToNow } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Date / time formatting ────────────────────────────────────────
// One simple, consistent format across the whole app:
//   formatDate      → "19 May 2026"
//   formatDateTime  → "19 May 2026, 3:52 PM"
//   formatTime      → "3:52 PM"  (accepts ISO datetimes or bare "HH:mm")
//   formatRelativeTime → "2 hours ago" / "in 3 days"
// All accept ISO strings, "YYYY-MM-DD", Date, epoch ms, or empty, and return
// the fallback ("—" by default) for missing/invalid input.
type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  // Date-only "YYYY-MM-DD" → parse as LOCAL midnight so the day never shifts via UTC.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isValid(d) ? d : null;
  }
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

export function formatDate(value: DateInput, fallback = "—"): string {
  const d = toDate(value);
  return d ? format(d, "dd MMM yyyy") : fallback;
}

export function formatDateTime(value: DateInput, fallback = "—"): string {
  const d = toDate(value);
  return d ? format(d, "dd MMM yyyy, h:mm a") : fallback;
}

export function formatTime(value: DateInput, fallback = "—"): string {
  // Support bare "HH:mm" / "HH:mm:ss" clock strings (e.g. activity.time).
  if (typeof value === "string") {
    const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
    if (m) {
      const d = new Date();
      d.setHours(Number(m[1]), Number(m[2]), 0, 0);
      return format(d, "h:mm a");
    }
  }
  const d = toDate(value);
  return d ? format(d, "h:mm a") : fallback;
}

export function formatRelativeTime(value: DateInput, fallback = "—"): string {
  const d = toDate(value);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : fallback;
}

export const formatCurrency = (v: number) => `₹${(v / 100000).toFixed(1)}L`;

export const EXPECTED_VALUE_RANGES = [
  { label: "₹50K – ₹1L",    value: 75000 },
  { label: "₹1L – ₹2.5L",   value: 175000 },
  { label: "₹2.5L – ₹5L",   value: 375000 },
  { label: "₹5L – ₹10L",    value: 750000 },
  { label: "₹10L – ₹25L",   value: 1750000 },
  { label: "₹25L – ₹50L",   value: 3750000 },
  { label: "₹50L – ₹75L",   value: 6250000 },
  { label: "₹75L – ₹1Cr",   value: 8750000 },
  { label: "₹1Cr+",         value: 10000000 },
];

// Real local-date today as YYYY-MM-DD. Evaluated at module load — accurate at app
// start, accurate across navigation, but stale if the user keeps the tab open across
// midnight (requires a refresh). Using local components (not toISOString) so users in
// IST near midnight don't get yesterday's date from UTC rollover.
function getLocalToday(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
export const TODAY: string = getLocalToday();
